import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from '@/lib/auth'
import App from './App.tsx'
import './index.css'
// Import react-phone-input-2 CSS globally to ensure it's always loaded
import 'react-phone-input-2/lib/style.css'
import { registerSyncfusionLicense } from '@/lib/syncfusion-license'
import { localStorageVariables } from '@/types/localStorageVariables';
import localizationService, { yardiTranslationConfig, initTranslations, translate } from 'yardi-react-translation/translationService';
import { ApplicationLoadingScreen } from '@/components/ui/ApplicationLoadingScreen'
import { getPropertyId } from '@/hooks/useApiSequencing';
yardiTranslationConfig.languageApiBase = '/guarantorportal';

yardiTranslationConfig.parameters = {
  propertyId: getPropertyId() || '0',
  productName: 'guarantorportal'
};
// Lazy load heavy dependencies
const initializeHeavyDependencies = async () => {
  // Lazy load FontAwesome
  await import('@/lib/fontawesome')
  // Lazy load global API configuration
  await import('@/lib/api')
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 10, // 10 minutes - increased from 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes garbage collection
      retry: 1, // Reduced retries to prevent excessive requests
      refetchOnWindowFocus: false, // Prevent refetch on window focus
      refetchOnReconnect: false, // Prevent refetch on reconnect
      refetchOnMount: false, // Prevent refetch on component mount if data exists
    },
  },
})

const TranslationLoader = () => <ApplicationLoadingScreen />

const renderApp = async () => {
  const root = ReactDOM.createRoot(document.getElementById('root')!)
  try {
    root.render(
      <React.StrictMode>
        <TranslationLoader />
      </React.StrictMode>
    )
    // Initialize translations
    try {
      console.log(' Initializing translations...');
      initTranslations();

      // Wait for translations with TIMEOUT
      const translationTimeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Translation loading timeout')), 5000)
      );

      await Promise.race([
        localizationService.waitForReady(),
        translationTimeout
      ]);
      
      console.log('Translations ready, rendering app');
    } catch (translationError) {
      // Translation failed - log it but CONTINUE
      console.warn('Translation loading failed or timed out:', translationError);
      console.warn('App will render without waiting for translations');
    }
    
    // Initialize heavy dependencies in the background
    initializeHeavyDependencies().catch(console.error);
    // syncfusion license
    try {
      registerSyncfusionLicense();
    } catch (err) {
      console.log('Failed to init syncfusion license:', err);
    }
  } catch (error) {
    console.error(' Error during app initialization:', error);
  } finally {
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  )
}
}

renderApp();